/* eslint-disable react/jsx-pascal-case */
import React, {useState, useRef, useEffect} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {PMarginSm, H2, H3, P} from '@/typography';
import {Table, TBody, TH, TR, TD} from '@/ui/tables';
import {
  withMarginBottomSm,
  withMarginBottomXl,
  withMarginBottomXxl,
  withMarginBottomMd,
  withMarginTopDoubleLg
} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '@/ui/legal/legal.helper';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import subprocessors from '../messages/en/subprocessors.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('subprocessors.seo.title'),
    description: seoT('subprocessors.seo.description'),
    canonical: seoT('subprocessors.seo.canonical')
  };
};

const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');
const BoldSpan = withBoldWeight('span');
const H3Margin = withMarginBottomSm(H3);
const DivMarginedBottomXl = withMarginBottomXl('div');
const MarginedH2 = withMarginBottomMd(H2);
const PriorVersionMargin = withMarginTopDoubleLg('div');

const SubprocessorsPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [activeTab, setActiveTab] = useState('sub-processors');
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('subprocessors');
  const legalT = useTranslations('legal');

  const tabItems = [
    {
      text: legalT('tabs.dataProtection.dataProcessingAddendum'),
      value: 'data-processing-addendum',
      child: false,
      routerPushUrl: '/legal/data-protection?tab=data-processing-addendum'
    },
    {
      text: legalT('tabs.dataProtection.ccpaCpra'),
      value: 'CCPA/CPRA',
      child: false,
      routerPushUrl: '/legal/data-protection?tab=CCPA/CPRA'
    },
    {
      text: legalT('tabs.dataProtection.subProcessors'),
      value: 'sub-processors',
      child: false,
      routerPushUrl: '/subprocessors'
    },
    {
      text: legalT('tabs.dataProtection.securityMeasures'),
      value: 'security-measures',
      child: false,
      routerPushUrl: '/legal/data-protection?tab=security-measures'
    }
  ];

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab={activeTab}
        title={legalT('sectionTitles.dataProtection')}
        tabItems={tabItems}
        setActiveTab={setActiveTab}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="data-protection"
          defaultValue={activeTab}
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router, setActiveTab)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  return (
    <>
      <LegalHero title={legalT('sectionTitles.dataProtection')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.dataProtection')}
                activeTab={tabItems.find(item => item.value === activeTab).text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <MarginedH2>{t('heading')}</MarginedH2>
                <DivMarginedBottomXl>
                  <Link
                    onClick={handlePrint}
                    variant="secondary-no-gradient-download"
                    className="no-print">
                    {t('downloadLabel')}
                  </Link>
                </DivMarginedBottomXl>
                <H3Margin className="heading-4">{t('workableSubprocessorsTitle')}</H3Margin>

                <Table className={legal['legal-table']}>
                  <TBody>
                    <TR>
                      <TH>{t('tableHeaders.subProcessor')}</TH>
                      <TH>{t('tableHeaders.corporateLocation')}</TH>
                      <TH>{t('tableHeaders.description')}</TH>
                    </TR>
                    {t.raw('subprocessors').map(row => (
                      <TR key={row.vendor}>
                        <TD>
                          <BoldSpan>{row.vendor}</BoldSpan>
                        </TD>
                        <TD>{row.location}</TD>
                        <TD>{row.description}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
                <H3Margin className="heading-4">{t('affiliatesTitle')}</H3Margin>
                <Table className={legal['legal-table']}>
                  <TBody>
                    <TR>
                      <TH>{t('tableHeaders.subProcessor')}</TH>
                      <TH>{t('tableHeaders.corporateLocation')}</TH>
                      <TH>{t('tableHeaders.description')}</TH>
                    </TR>
                    {t.raw('affiliates').map(row => (
                      <TR key={row.vendor}>
                        <TD>
                          <BoldSpan>{row.vendor}</BoldSpan>
                        </TD>
                        <TD>{row.location}</TD>
                        <TD>{row.description}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
                <Row>
                  <Col xs={12}>
                    <P>{t('footnote')}</P>
                    <PriorVersionMargin>
                      <PMarginSm className="heading-6">
                        <BoldSpan>{t('lastUpdated')}</BoldSpan>
                      </PMarginSm>
                    </PriorVersionMargin>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(SubprocessorsPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, subprocessors};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
